import React, { useEffect, useState, useRef } from "react";
import { Form, Modal, Button, Spinner, Badge } from "react-bootstrap";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import {
  Mail,
  Plus,
  Send,
  Sparkles,
  Trash2,
  Paperclip,
  PenTool,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Link as LinkIcon,
  RemoveFormatting,
  Heading1,
  Heading2,
  FileText,
  FileImage,
  File,
  X,
  Settings,
  Check,
} from "lucide-react";
import "./EmailClient.css";

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

const EmailClient = () => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");

  // Composer State
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);

  // Add Account Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newAcc, setNewAcc] = useState({
    email_address: "",
    smtp_host: "",
    smtp_port: 465,
    username: "",
    password: "",
    is_shared: false,
  });

  // AI Modal
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiContext, setAiContext] = useState("");
  const [aiLeadName, setAiLeadName] = useState("");
  const [generating, setGenerating] = useState(false);

  // Signature State
  const [showSigModal, setShowSigModal] = useState(false);
  const [sigConfig, setSigConfig] = useState(() => {
    try {
      const saved = localStorage.getItem("paj_email_sig");
      if (saved) return JSON.parse(saved);
    } catch (_) {}
    return {
      name: user?.name || "Export Director",
      title: "Senior Sales Director",
      phone: "+81 80 1234 5678",
      company: "Platinum Auto Japan",
      website: "www.platinumautojapan.com",
      address: "Saitama Prefecture, Yashio City, Midori Area 5-9-5, Japan 340-0808",
      autoInsert: true,
    };
  });

  useEffect(() => {
    fetchAccounts();
    // eslint-disable-next-line
  }, []);

  const fetchAccounts = async () => {
    try {
      const { data } = await api.get("/email/accounts");
      setAccounts(data);
      if (data.length > 0 && !selectedAccountId) setSelectedAccountId(data[0].id);
    } catch (err) {
      toast.error("Failed to load email accounts");
    }
  };

  const handleAddAccount = async (e) => {
    e.preventDefault();
    try {
      await api.post("/email/accounts", newAcc);
      toast.success("Account connected successfully!");
      setShowAddModal(false);
      fetchAccounts();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add account");
    }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm("Disconnect this email account?")) return;
    try {
      await api.delete(`/email/accounts/${id}`);
      toast.success("Account disconnected");
      if (selectedAccountId === id) setSelectedAccountId("");
      fetchAccounts();
    } catch (err) {
      toast.error("Failed to delete account");
    }
  };

  // ── WYSIWYG Formatting Helpers ─────────────────────────────────
  const execCmd = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      editorRef.current.focus();
    }
  };

  const handleInsertLink = () => {
    const url = window.prompt("Enter hyperlinked URL (e.g. https://www.platinumautojapan.com):");
    if (url) {
      execCmd("createLink", url);
    }
  };

  // ── Signature Builder & Inserter ───────────────────────────────
  const generateSignatureHTML = (sig) => {
    return `<br/><br/><div style="margin-top: 20px; padding-top: 12px; border-top: 1.5px solid #cbd5e1; font-family: 'Segoe UI', Arial, sans-serif; font-size: 13px; color: #334155;">
      <div style="font-weight: 700; font-size: 14.5px; color: #0b2545;">${sig.name || "Sales Representative"}</div>
      <div style="color: #64748b; font-size: 12px; margin-top: 2px;">${sig.title || "Export Sales Director"} · <strong>${sig.company || "Platinum Auto Japan"}</strong></div>
      <div style="color: #334155; font-size: 12px; margin-top: 4px;">
        <strong>Mobile / WhatsApp:</strong> ${sig.phone || "+81 80 1234 5678"} | <strong>Web:</strong> <a href="https://${sig.website}" style="color: #0284c7; text-decoration: none;">${sig.website}</a>
      </div>
      <div style="color: #94a3b8; font-size: 11px; margin-top: 4px;">${sig.address}</div>
    </div>`;
  };

  const handleInsertSignature = () => {
    if (!editorRef.current) return;
    const sigHtml = generateSignatureHTML(sigConfig);
    editorRef.current.innerHTML += sigHtml;
    toast.success("Signature appended to email!");
  };

  const handleSaveSignature = () => {
    localStorage.setItem("paj_email_sig", JSON.stringify(sigConfig));
    toast.success("Default signature updated!");
    setShowSigModal(false);
  };

  // ── Attachments Handling ────────────────────────────────────────
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    files.forEach((file) => {
      // 10MB limit per file
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File ${file.name} is too large (> 10MB)`);
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64Data = event.target.result.split(",")[1];
        setAttachments((prev) => [
          ...prev,
          {
            filename: file.name,
            content: base64Data,
            encoding: "base64",
            contentType: file.type || "application/octet-stream",
            size: file.size,
          },
        ]);
      };
      reader.readAsDataURL(file);
    });

    // Reset input so same file can be re-selected if removed
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // ── Send Email ──────────────────────────────────────────────────
  const handleSend = async () => {
    if (!selectedAccountId) return toast.error("Please select a sender email account");
    if (!to.trim()) return toast.error("Please enter a recipient email address");
    if (!subject.trim()) return toast.error("Please enter an email subject line");

    const bodyHtml = editorRef.current ? editorRef.current.innerHTML.trim() : "";
    if (!bodyHtml || bodyHtml === "<br>") return toast.error("Please type your email message body");

    setSending(true);
    try {
      await api.post("/email/send", {
        accountId: selectedAccountId,
        to: to.trim(),
        subject: subject.trim(),
        html: bodyHtml,
        attachments: attachments.map((att) => ({
          filename: att.filename,
          content: att.content,
          encoding: "base64",
          contentType: att.contentType,
        })),
      });

      toast.success("Email sent successfully!");
      setTo("");
      setSubject("");
      setAttachments([]);
      if (editorRef.current) editorRef.current.innerHTML = "";
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const handleDiscard = () => {
    if (!window.confirm("Discard current draft and attachments?")) return;
    setTo("");
    setSubject("");
    setAttachments([]);
    if (editorRef.current) editorRef.current.innerHTML = "";
    toast.success("Draft discarded");
  };

  const handleGenerateAI = async () => {
    if (!aiContext) return toast.error("Please provide context for the AI drafter");
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/draft-email", {
        context: aiContext,
        leadName: aiLeadName,
      });
      setSubject(data.subject || "Regarding Your Vehicle Inquiry - Platinum Auto Japan");
      if (editorRef.current) {
        editorRef.current.innerHTML = (data.body || "").replace(/\n/g, "<br/>");
      }
      toast.success("Draft generated!");
      setShowAIModal(false);
      setAiContext("");
      setAiLeadName("");
    } catch (err) {
      toast.error("Failed to generate AI draft");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="email-client">
      {/* ── Left Sidebar (Accounts & Settings) ── */}
      <div className="ec-sidebar">
        <div className="ec-header">
          <h5>
            <Mail size={18} className="me-2 text-primary" /> Email Accounts
          </h5>
          <button
            className="btn btn-sm btn-primary d-flex align-items-center gap-1"
            onClick={() => setShowAddModal(true)}
            title="Connect an SMTP Account"
          >
            <Plus size={15} /> <span>Connect</span>
          </button>
        </div>

        <div className="ec-accounts">
          <p className="text-muted small fw-bold mb-2 text-uppercase letter-spacing-1">
            Sender Profiles ({accounts.length})
          </p>
          {accounts.length === 0 ? (
            <p className="text-muted small fst-italic">No accounts configured yet. Click Connect to add your SMTP mailbox.</p>
          ) : null}
          {accounts.map((acc) => (
            <div
              key={acc.id}
              className={`ec-account-item ${selectedAccountId === acc.id ? "active" : ""}`}
              onClick={() => setSelectedAccountId(acc.id)}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="ec-account-email text-truncate">{acc.email_address}</div>
                <div className="ec-account-tag">
                  {acc.is_shared ? (
                    <Badge bg="info" className="fw-normal">Shared Account</Badge>
                  ) : (
                    <Badge bg="secondary" className="fw-normal">Personal</Badge>
                  )}
                </div>
              </div>
              {(user?.role === "Admin" || acc.employeeId === user?.id) && (
                <button
                  className="ec-del-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteAccount(acc.id);
                  }}
                  title="Remove account"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="ec-sidebar-footer">
          <button
            type="button"
            className="btn-sidebar-signature"
            onClick={() => setShowSigModal(true)}
          >
            <Settings size={14} /> Configure My Signature
          </button>
        </div>
      </div>

      {/* ── Right Main Area (Gmail-Style Composer) ── */}
      <div className="ec-main">
        <div className="ec-composer">
          {/* Header */}
          <div className="ec-composer-header">
            <h4>
              <Mail className="text-primary" size={18} /> Compose New Message
            </h4>
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-dark d-flex align-items-center gap-2 rounded-pill px-3 py-1 shadow-sm small"
                onClick={() => setShowAIModal(true)}
              >
                <Sparkles size={14} className="text-warning" /> AI Draft Assistant
              </button>
            </div>
          </div>

          {/* Form Fields: From, To, Subject */}
          <div className="ec-fields-strip">
            <div className="ec-field-row">
              <span className="ec-field-label">From:</span>
              <Form.Select
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                className="ec-field-input"
              >
                <option value="">-- Choose sender address --</option>
                {accounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.email_address} {acc.is_shared ? "(Shared Mailbox)" : "(Personal)"}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="ec-field-row">
              <span className="ec-field-label">To:</span>
              <Form.Control
                type="email"
                className="ec-field-input"
                placeholder="client@example.com (buyer, broker, consignee...)"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>

            <div className="ec-field-row">
              <span className="ec-field-label">Subject:</span>
              <Form.Control
                type="text"
                className="ec-field-input fw-semibold"
                placeholder="e.g. Pro-Forma Invoice & Vehicle Confirmation - HONDA CR-V"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
          </div>

          {/* Gmail-Style Rich Text Toolbar */}
          <div className="ec-toolbar">
            <button
              type="button"
              className="ec-tool-btn"
              onClick={() => execCmd("bold")}
              title="Bold (Ctrl+B)"
            >
              <Bold size={15} />
            </button>
            <button
              type="button"
              className="ec-tool-btn"
              onClick={() => execCmd("italic")}
              title="Italic (Ctrl+I)"
            >
              <Italic size={15} />
            </button>
            <button
              type="button"
              className="ec-tool-btn"
              onClick={() => execCmd("underline")}
              title="Underline (Ctrl+U)"
            >
              <Underline size={15} />
            </button>
            <button
              type="button"
              className="ec-tool-btn"
              onClick={() => execCmd("strikeThrough")}
              title="Strikethrough"
            >
              <Strikethrough size={15} />
            </button>

            <div className="ec-tool-divider" />

            <button
              type="button"
              className="ec-tool-btn"
              onClick={() => execCmd("formatBlock", "<h1>")}
              title="Header 1"
            >
              <Heading1 size={15} />
            </button>
            <button
              type="button"
              className="ec-tool-btn"
              onClick={() => execCmd("formatBlock", "<h2>")}
              title="Header 2"
            >
              <Heading2 size={15} />
            </button>
            <button
              type="button"
              className="ec-tool-btn"
              onClick={() => execCmd("formatBlock", "<p>")}
              title="Normal Paragraph"
            >
              P
            </button>

            <div className="ec-tool-divider" />

            <button
              type="button"
              className="ec-tool-btn"
              onClick={() => execCmd("insertUnorderedList")}
              title="Bullet List"
            >
              <List size={15} />
            </button>
            <button
              type="button"
              className="ec-tool-btn"
              onClick={() => execCmd("insertOrderedList")}
              title="Numbered List"
            >
              <ListOrdered size={15} />
            </button>
            <button
              type="button"
              className="ec-tool-btn"
              onClick={() => execCmd("formatBlock", "<blockquote>")}
              title="Blockquote"
            >
              <Quote size={14} />
            </button>

            <div className="ec-tool-divider" />

            <button
              type="button"
              className="ec-tool-btn"
              onClick={handleInsertLink}
              title="Insert Link"
            >
              <LinkIcon size={14} />
            </button>
            <button
              type="button"
              className="ec-tool-btn"
              onClick={() => execCmd("removeFormat")}
              title="Clear Formatting"
            >
              <RemoveFormatting size={15} />
            </button>
          </div>

          {/* ── WYSIWYG Message Body (ContentEditable) ── */}
          <div
            ref={editorRef}
            className="ec-editor-body"
            contentEditable
            suppressContentEditableWarning
            placeholder="Type your official commercial message here... Format text, paste images, or attach vehicle files below."
          />

          {/* ── Attachments Tray ── */}
          {attachments.length > 0 && (
            <div className="ec-attachments-tray">
              <span className="small text-muted fw-bold text-uppercase d-flex align-items-center gap-1">
                <Paperclip size={13} /> Attached Files ({attachments.length}):
              </span>
              {attachments.map((file, idx) => (
                <div key={idx} className="ec-attachment-pill">
                  {file.filename.endsWith(".pdf") ? (
                    <FileText size={15} className="text-danger" />
                  ) : file.filename.match(/\.(jpg|jpeg|png|webp)$/i) ? (
                    <FileImage size={15} className="text-info" />
                  ) : (
                    <File size={15} className="text-primary" />
                  )}
                  <span className="att-name" title={file.filename}>
                    {file.filename}
                  </span>
                  <span className="att-size">({formatFileSize(file.size)})</span>
                  <button
                    type="button"
                    className="ec-attachment-remove"
                    onClick={() => handleRemoveAttachment(idx)}
                    title="Remove attachment"
                  >
                    <X size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* ── Bottom Action Bar (Gmail Style) ── */}
          <div className="ec-composer-footer">
            <div className="ec-footer-actions-left">
              <button
                type="button"
                className="btn-gmail-send"
                onClick={handleSend}
                disabled={sending}
              >
                {sending ? <Spinner size="sm" /> : <Send size={15} />}
                <span>{sending ? "Sending Remittance..." : "Send Email"}</span>
              </button>

              {/* Hidden file input */}
              <input
                type="file"
                multiple
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />

              <button
                type="button"
                className="btn-composer-icon"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                title="Attach Pro-Forma Invoice, Inspection Sheet, or Car Photos"
              >
                <Paperclip size={15} />
                <span>Attach Files</span>
                {attachments.length > 0 && (
                  <Badge bg="primary" pill>
                    {attachments.length}
                  </Badge>
                )}
              </button>

              <button
                type="button"
                className="btn-composer-icon"
                onClick={handleInsertSignature}
                title="Append your corporate email signature"
              >
                <PenTool size={14} />
                <span>Insert Signature</span>
              </button>
            </div>

            <button
              type="button"
              className="btn-composer-discard"
              onClick={handleDiscard}
              title="Discard draft"
            >
              <Trash2 size={17} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Connect Email Account Modal ── */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h5 fw-bold">Connect SMTP Mailbox</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddAccount}>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">Email Address</Form.Label>
              <Form.Control
                required
                type="email"
                placeholder="sales@platinumautojapan.com"
                value={newAcc.email_address}
                onChange={(e) => setNewAcc({ ...newAcc, email_address: e.target.value })}
              />
            </Form.Group>
            <div className="row g-2">
              <div className="col-8">
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-semibold">SMTP Host</Form.Label>
                  <Form.Control
                    required
                    placeholder="smtp.gmail.com or smtp.emailsrvr.com"
                    value={newAcc.smtp_host}
                    onChange={(e) => setNewAcc({ ...newAcc, smtp_host: e.target.value })}
                  />
                </Form.Group>
              </div>
              <div className="col-4">
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-semibold">Port</Form.Label>
                  <Form.Control
                    required
                    type="number"
                    value={newAcc.smtp_port}
                    onChange={(e) => setNewAcc({ ...newAcc, smtp_port: Number(e.target.value) })}
                  />
                </Form.Group>
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">SMTP Username</Form.Label>
              <Form.Control
                required
                placeholder="Username or full email"
                value={newAcc.username}
                onChange={(e) => setNewAcc({ ...newAcc, username: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">SMTP App Password</Form.Label>
              <Form.Control
                required
                type="password"
                placeholder="Generated App Password"
                value={newAcc.password}
                onChange={(e) => setNewAcc({ ...newAcc, password: e.target.value })}
              />
            </Form.Group>
            {user?.role === "Admin" && (
              <Form.Check
                type="checkbox"
                id="shared-check"
                label="Make this a Shared Account (All agents can send quotes through it)"
                checked={newAcc.is_shared}
                onChange={(e) => setNewAcc({ ...newAcc, is_shared: e.target.checked })}
              />
            )}
            <Button type="submit" variant="primary" className="w-100 mt-3 fw-semibold">
              Save &amp; Connect Mailbox
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ── Corporate Signature Studio Modal ── */}
      <Modal show={showSigModal} onHide={() => setShowSigModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="h5 fw-bold d-flex align-items-center gap-2">
            <PenTool size={18} className="text-primary" /> Corporate Signature Studio
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Label className="small fw-bold text-muted">Your Full Name</Form.Label>
              <Form.Control
                size="sm"
                value={sigConfig.name}
                onChange={(e) => setSigConfig({ ...sigConfig, name: e.target.value })}
              />
            </div>
            <div className="col-md-6">
              <Form.Label className="small fw-bold text-muted">Corporate Title</Form.Label>
              <Form.Control
                size="sm"
                value={sigConfig.title}
                onChange={(e) => setSigConfig({ ...sigConfig, title: e.target.value })}
              />
            </div>
            <div className="col-md-6">
              <Form.Label className="small fw-bold text-muted">Direct WhatsApp / Phone</Form.Label>
              <Form.Control
                size="sm"
                value={sigConfig.phone}
                onChange={(e) => setSigConfig({ ...sigConfig, phone: e.target.value })}
              />
            </div>
            <div className="col-md-6">
              <Form.Label className="small fw-bold text-muted">Company Name</Form.Label>
              <Form.Control
                size="sm"
                value={sigConfig.company}
                onChange={(e) => setSigConfig({ ...sigConfig, company: e.target.value })}
              />
            </div>
            <div className="col-md-6">
              <Form.Label className="small fw-bold text-muted">Official Website</Form.Label>
              <Form.Control
                size="sm"
                value={sigConfig.website}
                onChange={(e) => setSigConfig({ ...sigConfig, website: e.target.value })}
              />
            </div>
            <div className="col-md-6">
              <Form.Label className="small fw-bold text-muted">Office Address</Form.Label>
              <Form.Control
                size="sm"
                value={sigConfig.address}
                onChange={(e) => setSigConfig({ ...sigConfig, address: e.target.value })}
              />
            </div>

            <div className="col-12 mt-3">
              <span className="small fw-bold text-uppercase text-muted">Live Signature Preview:</span>
              <div
                className="signature-preview-card mt-2 shadow-sm"
                dangerouslySetInnerHTML={{ __html: generateSignatureHTML(sigConfig) }}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowSigModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" className="fw-semibold px-4 d-flex align-items-center gap-1" onClick={handleSaveSignature}>
            <Check size={16} /> Save Signature
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── AI Email Drafter Modal ── */}
      <Modal show={showAIModal} onHide={() => setShowAIModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h5 fw-bold d-flex align-items-center gap-2">
            <Sparkles size={18} className="text-warning" /> Generate Email with AI
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold">Client Name (Optional)</Form.Label>
            <Form.Control
              placeholder="e.g. John Doe / MKS Brokerage"
              value={aiLeadName}
              onChange={(e) => setAiLeadName(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold">Instructions / Key Points</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="e.g. Thank them for the wire transfer, confirm the shipment booking on the vessel for Tokyo to Nassau, and attach the Bill of Lading..."
              value={aiContext}
              onChange={(e) => setAiContext(e.target.value)}
            />
          </Form.Group>
          <Button
            variant="primary"
            className="w-100 fw-semibold d-flex align-items-center justify-content-center gap-2"
            onClick={handleGenerateAI}
            disabled={generating}
          >
            {generating ? <Spinner size="sm" /> : <Sparkles size={16} />}
            <span>{generating ? "Drafting with AI..." : "Generate Draft"}</span>
          </Button>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default EmailClient;
